/**
 * authenticator provides functions to login or create an account
 */

import jsSHA from 'jssha'
import { get } from 'lodash'
import moment from 'moment'
import axios from 'axios'
import { me } from './GraphQlClientService'
import { storageMode, trackSignedUp, getBranchAttribution, getAdAttribution } from '../helpers'
import { formatAndRethrowError, getAccessToken } from './GraphQlClientService'

const _localStorage = storageMode('localStorage')
const _sessionStorage = storageMode('sessionStorage')

const CLIENT_ID = process.env.NEXT_PUBLIC_CLIENT_ID
const CLIENT_SECRET = process.env.NEXT_PUBLIC_CLIENT_SECRET
const STARBASE_URL = process.env.NEXT_PUBLIC_STARBASE_URL

/**
 * error code used to signal that a provider (Google/Apple) id_token has no
 * matching account, so the caller can route the user into sign-up instead
 */
export const NO_ACCOUNT = 'NO_ACCOUNT'

/**
 * Machine-readable codes Starbase sets on `errors[0].code`. These are the preferred
 * signal — the message-text checks below are fallbacks that work against the current
 * server, which does not send a code yet. `errors[0]` is used rather than a top-level
 * field because Starbase's error handler reuses `err.code` as the HTTP status (app.js).
 */
const WIRE_NO_ACCOUNT = 'no_account'
const WIRE_PROOF_EXPIRED = 'social_proof_expired'

const firstServerError = (error) => error?.response?.data?.errors?.[0] || {}

/**
 * isNoAccountResponse: does this rejection mean "the provider identity is valid but has
 * no Glamsquad account yet" (→ route to signup) rather than a real auth failure?
 *
 * Checked in order of robustness: the wire code, then the signup handle (only the
 * no-account response carries one — auth.js), then the message text.
 * @param {any} error
 * @returns {boolean}
 */
const isNoAccountResponse = (error) => {
  const data = error?.response?.data || {}
  if (firstServerError(error).code === WIRE_NO_ACCOUNT) { return true }
  if (data.signupToken) { return true }
  const message = (firstServerError(error).message || '').toLowerCase()
  return message.includes('user not found') || message.includes('no account')
}

/**
 * isEmailAlreadyExists: did a create-account request fail because the email is already
 * registered to an existing account (→ route the user into email sign-in) rather than
 * some other validation failure?
 *
 * Starbase enforces this with a unique constraint on the users.email column, so the
 * rejection arrives as a SequelizeUniqueConstraintError serialized to
 * `errors[0] = { path: 'email', type: 'unique violation', message, value }` (app.js).
 * Checked in order of robustness: the structured path/type, then the message text.
 * @param {any} error
 * @returns {boolean}
 */
export const isEmailAlreadyExists = (error) => {
  const first = firstServerError(error)
  if (first.path === 'email' && first.type === 'unique violation') { return true }
  return (first.message || '').toLowerCase().includes('already have an account with that email')
}

/**
 * isAccountLocked: did this auth request fail because the identifier's only accounts are
 * locked (→ tell the user the account is deactivated) rather than because no account exists?
 *
 * Starbase's OTP exchange answers an all-locked match with a validation error typed
 * `accountLocked` instead of the empty token list that means "no account yet" (its
 * client-otp-auth skill). Keyed on `errors[].type`, the same contract booking reads for
 * `searchExpired` / `providerBooked` — never on the message, which is display copy.
 * @param {any} error
 * @returns {boolean}
 */
export const isAccountLocked = (error) =>
  (error?.response?.data?.errors || []).some((item) => item?.type === 'accountLocked')

/**
 * isProviderProofExpired: did a provider signup fail because the proof itself is no longer
 * usable, rather than because of something the user can fix on the form?
 *
 * Nothing on the create-account screen can recover from this — the user has to re-authorize
 * with the provider — so callers should return them to the entry screen.
 *
 * The proof outlives the form by a limited window: Google's signup handle has a server-side
 * TTL (util/social-signup.js) and Apple's id_token carries Apple's own `exp`. Google's
 * expiry surfaces as a 400 'Social signup token is invalid or expired.'; Apple's currently
 * escapes Starbase's create hook as a raw 'jwt expired' that its error handler re-wraps as
 * a 500, hence the second check.
 * @param {any} error
 * @returns {boolean}
 */
export const isProviderProofExpired = (error) => {
  if (firstServerError(error).code === WIRE_PROOF_EXPIRED) { return true }
  const message = (firstServerError(error).message || '').toLowerCase()
  // matched narrowly on purpose: a bare 'invalid or expired' would also catch the OTP
  // flow's 'Invalid or expired code'
  return message.includes('signup token is invalid or expired') || message.includes('jwt expired')
}

/**
 * sign into an account and set access token in local storage
 *
 * @param {*} email
 * @param {*} password
 * @returns
 */
export const signIn = async (email, password, rememberAuth) => {
  if (email & password) {
    throw new Error('Enter your email and password.')
  }

  const res = await makeSignedAPICall('POST', '/api/v1/auth/token', {
    username: email.trim().toLowerCase(),
    password: password,
    grant_type: 'password'
  })

  const { access_token: accessToken } = res

  if (rememberAuth) {
    _localStorage.setItem('accessToken', accessToken)
    _localStorage.setItem('lastUpdated', moment().unix())
  }

  _sessionStorage.setItem('accessToken', accessToken)
  _sessionStorage.setItem('lastUpdated', moment().unix())

  return me()
}


/**
 * create an account
 *
 * @param {*} email
 * @param {*} nameFirst
 * @param {*} nameLast
 * @param {*} phone
 * @param {*} password
 * @param {string} referredByCode
 */
export const signUp = async (
  email,
  nameFirst,
  nameLast,
  phone,
  referredByCode,
  oneTimePasscode,
  password,
  channel = 'phone'
) => {
  signOut()
  return makeSignedAPICall('POST', '/api/v1/users/', {
    email,
    nameFirst,
    nameLast,
    phone,
    referredByCode,
    oneTimePasscode,
    password
  })
    .then(res => {
      const branchAttribution = getBranchAttribution()
      const adAttribution = getAdAttribution()
      trackSignedUp({
        email,
        first_name: nameFirst,
        last_name: nameLast,
        phone,
        username: email,
      }, res.data.id, branchAttribution, adAttribution,
      // `channel` ('email' | 'phone') lands in trackSignedUp's `type` param → emits { type }
      channel)

      if (oneTimePasscode && !password) {
        const destination = channel === 'email' ? email : phone
        return exchangeOTPForTokens(oneTimePasscode, channel, destination)
          .then((tokens) => {
            // find the token for the account we just created (email is unique)
            const newToken = tokens.find((t) => t.email === email.toLowerCase())
            return loginWithToken(newToken.access_token)
          })
      }

      return signIn(email, password)
    })
}

/**
 * Kick off a passwordless login by sending an OTP to the given channel.
 * @param {'phone'|'email'} channel
 * @param {string} destination phone (E.164) or email
 */
export const passwordlessStart = async (channel, destination) => {
  const body = channel === 'email' ? { email: destination } : { phone: destination }
  return makeSignedAPICall('POST', '/api/v1/auth/passwordless/start', body)
}

/**
 * sends password reset email to given email
 * given POST method will send email to user
 * given PUT will update user with body
 *
 * @param {object} body
 * * @param {string} method
 */
export const resetPasswordEmail = async (method, body) => {
  try {
    await makeSignedAPICall(method, '/api/v1/users/password-reset', body)
  } catch (e) {
    console.error(e)
  }
}

export const resetPassword = async (password, token, isProvider) => {
  try {
    const body = { password }
    // provider endpoint is expecting there to be a key in the body instead of a token
    if (isProvider) {
      body.key = token
    } else {
      body.token = token
    }

    await makeSignedAPICall('PUT', '/api/v1/users/password-reset', body)
  } catch (error) {    
    console.log(error.response.data)
    formatAndRethrowError(error.response.data)
  }
}

/**
 * fetches a user by their referralCode
 *
 * @param {string} code
 */
export const findByReferralCode = async code =>
  await makeSignedAPICall('GET', `/api/v1/referrals/${code}/sender`)

/**
 * adds client signature header and makes request
 *
 * @param {*} method
 * @param {*} endpoint
 * @param {*} body
 * @returns
 */
export const makeSignedAPICall = async (method, endpoint, body) => {
  const sha = new jsSHA('SHA-256', 'TEXT')
  sha.setHMACKey(CLIENT_SECRET, 'TEXT')
  // Starbase signs JSON.stringify(req.body), and its body parser has already turned a bodyless
  // request into {} by the time it verifies. Default to {} so bodyless calls agree with the
  // server: JSON.stringify(undefined) returns undefined, which Array.join renders as '', giving
  // a plaintext ending in '|' where the server produced '|{}'.
  sha.update([method, endpoint, JSON.stringify(body ?? {})].join('|'))
  const hmac = sha.getHMAC('B64')

  const url = STARBASE_URL + endpoint

  try {
    const res = await axios({
      method,
      url,
      headers: {
        Authorization: 'Glam ' + [CLIENT_ID, hmac].join(':'),
        'Content-Type': 'application/json'
      },

      data: body,
      timeout: 15000,
      responseType: 'json'
    })

    if (res.status > 399) {
      const err = new Error(
        get(res, 'data.errors.0.message') || 'Error making request.'
      )
      err.res = res
      err.body = res.data

      throw err
    }

    return res.data
  } catch (error) {
    // log only the message: the full axios error carries error.config.data (the request body,
    // which for the OTP exchange includes the code) and must never be logged. warn (not error)
    // so an expected 4xx doesn't trip Next's dev error overlay.
    console.warn('makeSignedAPICall', error?.message ?? error)
    throw error
  }
}

export const signOut = () => {
  _localStorage.removeItem('accessToken')
  _localStorage.removeItem('lastUpdated')
  _localStorage.removeItem('userInfo')
  _localStorage.removeItem('book')
  _sessionStorage.removeItem('accessToken')
  _sessionStorage.removeItem('book')

  return null
}
export const isAuthenticated = () =>
  !!_localStorage.getItem('accessToken') ||
  !!_sessionStorage.getItem('accessToken')

/* exchangeCodeForToken
 * called to logic from a magic link from marketing
 * different from exchangeOTPCode
 */
export const exchangeCodeForToken = async (code) => {
  console.log('exchanging...', code)
  try {
    const { access_token } = await makeSignedAPICall('POST', '/api/v1/auth/token', {
      grant_type: 'authorization_code',
      code: code.replace(/\s/g, '+')
    })

    saveAccessToken(access_token)
    return me()
  } catch (e) {
    console.error('exchangeCodeForToken', e)
    throw e
  }
}

/* exchangeOTPCode
 * returns all access tokens associated to a phone number for account selection
 */
/**
 * Exchange an OTP code for the access token(s) tied to the given channel.
 * Returns all tokens associated with the phone/email for account selection.
 * @param {string} code
 * @param {'phone'|'email'} channel
 * @param {string} destination phone (E.164) or email
 */
export const exchangeOTPForTokens = async (code, channel, destination) => {
  // makeSignedAPICall already logs (message only) and throws on failure, so no local catch is
  // needed here — dropping it also removes a duplicate log that exposed the OTP code.
  const identifier = channel === 'email' ? { email: destination } : { phone: destination }
  return makeSignedAPICall('POST', '/api/v1/auth/token', {
    code,
    ...identifier,
    grant_type: 'OTP_code'
  })
}

/**
 * Exchange a provider credential (verified server-side) for the access token
 * tied to the account it belongs to. Google sends `{ code }` (an auth-code the
 * server exchanges with Google); Apple sends `{ idToken, nonce }`.
 * @param {'google'|'apple'} provider
 * @param {{code?: string, idToken?: string, nonce?: string}} credential the provider proof, verified server-side
 * @returns {Promise<{access_token: string, token_type: string}>}
 * @throws {Error} rethrown unchanged for any failure other than "no account";
 *   on a 401 no-account response, throws an Error with `code === NO_ACCOUNT` plus
 *   the server-verified `idToken` + `profile` needed to complete signup
 */
export const exchangeProviderToken = async (provider, credential) => {
  try {
    return await makeSignedAPICall('POST', '/api/v1/auth/token', {
      grant_type: provider,
      ...credential,
    })
  } catch (error) {
    const status = error?.response?.status
    if (status === 401 && isNoAccountResponse(error)) {
      const data = error?.response?.data || {}
      const noAccount = new Error(NO_ACCOUNT)
      noAccount.code = NO_ACCOUNT
      noAccount.provider = provider
      noAccount.signupToken = data.signupToken
      noAccount.idToken = data.idToken
      noAccount.profile = data.profile
      throw noAccount
    }
    throw error
  }
}

/**
 * Create an account from a verified provider identity, then exchange for an
 * access token and log the new user in. Google proves the identity with the
 * opaque server-minted `signupToken` from the no-account response; Apple reuses
 * its browser-held, server-verifiable `idToken`.
 * @param {'google'|'apple'} provider
 * @param {{signupToken?: string, idToken?: string, nonce?: string}} credential the signup proof
 * @param {object} profile
 * @param {string} profile.nameFirst
 * @param {string} profile.nameLast
 * @param {string} profile.phone
 * @param {string} profile.email
 * @param {string} profile.referredByCode
 * @returns {Promise<object>} the logged-in user (via `me`)
 */
export const signUpWithProvider = async (
  provider,
  { signupToken, idToken, nonce },
  { nameFirst, nameLast, phone, email, referredByCode },
) => {
  signOut()

  // Google proves signup with the opaque server-minted handle; Apple reuses its verified
  // id_token + nonce (the nonce lets the server enforce replay protection on the id_token).
  // The same proof then logs the new account in: the server reads the handle without
  // consuming it at signup, and the login exchange is what consumes it.
  const proof = provider === 'google' ? { signupToken } : { idToken, nonce }

  const createRes = await makeSignedAPICall('POST', '/api/v1/users/', {
    email,
    nameFirst,
    nameLast,
    phone,
    referredByCode,
    socialSignUpData: { provider, ...proof },
  })

  const branchAttribution = getBranchAttribution()
  const adAttribution = getAdAttribution()
  trackSignedUp(
    { email, first_name: nameFirst, last_name: nameLast, phone, username: email },
    createRes.data.id,
    branchAttribution,
    adAttribution,
    provider,
  )

  const { access_token } = await exchangeProviderToken(provider, proof)
  return loginWithToken(access_token)
}

export const saveAccessToken = (access_token, rememberAuth = true) => {
  if (rememberAuth) {
    _localStorage.setItem('accessToken', access_token)
    _localStorage.setItem('lastUpdate', moment().unix())
  } else {
    _sessionStorage.setItem('accessToken', access_token)
    _sessionStorage.setItem('lastUpdated', moment().unix())
  }
}

export const loginWithToken = async (token) => {
  try {
    saveAccessToken(token)
    return me()
  } catch (e) {
    console.error('loginWithToken', e)
    throw e
  }
}

// sets the access token to the agent parameter and returns the standard me
// used when linking from starbase appointment detail to client survey
// allows agents to answer surveys for a client
export const authenticateAgent = (agent) => {
  try {
    _sessionStorage.setItem('accessToken', agent)
    return me()
  } catch (error) {
    console.error('authenticateAgent', error)
    throw error
  }
}

export const fetchIntercomJWT = async (userId) => {
  // access token if we have on
  const accessToken = getAccessToken()

  if (!accessToken) {
    return
  }

  const res = await fetch(
    STARBASE_URL.concat(`/api/v1/users/${userId}/intercom-jwt`),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer `.concat(accessToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({}),
      timeout: 20000,
      responseType: 'json'
    }
  )

  if (res.status > 299) {
    console.error('Error fetching Intercom JWT')
    return null
  }

  const { data: { token }} = await res.json()

  return token
};
